import { formatDate, formatDateCourte } from '../../api'
// Helpers partages de la facette producteur du module Marche (v7.1 sujet 3).

export const ORANGE = 'rgb(var(--c-primary))'
export const TEAL = 'rgb(var(--c-teal))'
export const VERT = 'rgb(var(--c-green))'
export const VERT_OK = '#0a8a5f'

export const eur = (n) => `${Number(n || 0).toFixed(2).replace('.', ',')} €`

/**
 * Ces deux helpers formatent des JOURS CALENDAIRES (`Event.date`,
 * `ModuleInvoice.dateEmission` — colonnes `@db.Date`). Ils delegent donc aux
 * formateurs UTC de `api.js` : rendus avec le fuseau du navigateur, ils
 * affichaient la VEILLE en Martinique (UTC-4). Voir la regle en tete de `api.js`
 * et `docs/DESIGN-GUIDE.md` §11.10.
 *
 * Pour un vrai instant (`inscriptionDe`, `repartitionAt`...), ne PAS passer par
 * ici : le rendu local est alors le bon.
 */
export const fmtDateCourte = formatDateCourte

/**
 * « Samedi 15 aout » — jour de semaine CAPITALISE, sans l'annee en cours
 * (cf. AUDIT-DESIGN-MARCHE.md, M2). `formatDate` capitalise deja.
 */
export const fmtDateLongue = formatDate

export const fmtHoraire = (m) =>
  m?.heureDebut ? `${m.heureDebut}${m.heureFin ? ` – ${m.heureFin}` : ''}` : null

/** Une offre est-elle au canal Marche actif ? (module principal ou canal additionnel) */
export const estCanalMarche = (o) =>
  o?.module?.code === 'MARCHE' ||
  (o?.channels || []).some((c) => c?.module?.code === 'MARCHE' && c?.isActive)

/**
 * PRIX AU CANAL EVENEMENTS (S75) — celui que le client paie au marche. Une
 * offre dont le canal principal est un autre (ex. Paniers, cree au BO) porte
 * son prix Evenements dans le canal additionnel (`prixCanal`) : c'est lui que
 * la feuille ✏️ ecrit, c'est donc lui qu'il faut lire partout. S77 (ADR-072) :
 * PLUS DE REPLI sur le prix de base (au canal Paniers c'est le NET du
 * producteur, pas un prix de stand) — null = « prix a saisir ».
 * Meme regle cote serveur (public.service `prixMarche`).
 */
export const prixMarche = (o) => {
  if (o?.module?.code === 'MARCHE') return Number(o.prix || 0)
  const canal = (o?.channels || []).find((c) => c?.module?.code === 'MARCHE' && c?.isActive)
  return canal?.prixCanal != null ? Number(canal.prixCanal) : null
}
/**
 * Taux de commission au canal Evenements (%) : celui du MARCHE (10 % par
 * defaut, ADR-058 §5), jamais le `commissionRate` de l'offre (30 % par defaut
 * au BO). Meme regle que `canauxTarifesDeLOffre` (producer/canaux.js).
 */
export const tauxMarche = (_o, tauxModule = 10) => Number(tauxModule)
/** Ce que le producteur recoit au marche : prix client x (1 - taux), TVA comprise (ADR-058 §5). */
export const netMarche = (o) => {
  const pv = prixMarche(o)
  return pv == null ? null : Math.round(pv * (1 - tauxMarche(o) / 100) * 100) / 100
}
/** Montant ou « prix a saisir » quand le canal Evenements n'a pas de prix (S77). */
export const eurOuASaisir = (n) => (n == null ? 'prix à saisir (✏️)' : eur(n))

/** Le produit fait-il partie du stand de REFERENCE ? (standDefaut du canal MARCHE, defaut oui) */
export const estStandDefaut = (o) => {
  const canal = (o?.channels || []).find((c) => c?.module?.code === 'MARCHE')
  return canal ? canal.standDefaut !== false : true
}

/** Emoji/visuel d'une offre (categorie du referentiel, repli generique). */
export const offreEmoji = (o) => o?.product?.category?.emoji || '🥭'
export const offreNom = (o) => o?.product?.nom || o?.nom || 'Produit'

/**
 * Etat de la fenetre d'inscription d'un evenement, cote producteur.
 *
 * VIT ICI ET NULLE PART AILLEURS (S60). Elle etait enfermee dans
 * `pages/producer/EventCard.jsx`, un composant qui ne sert QU'A la page
 * `PageMarchesProducteur`. L'onglet « Evenements » du module — celui que le
 * producteur utilise reellement — ne la voyait pas, et n'offrait donc AUCUNE
 * desinscription, alors que l'API l'autorise depuis la v0.2.0.
 *
 * S'INSCRIRE et SE RETIRER ne suivent pas la meme regle (S36) :
 *  - la CLOTURE ferme les deux portes (« contactez Manrina ») ;
 *  - AVANT l'ouverture, un producteur deja inscrit — le BO peut l'inscrire a
 *    tout moment — doit pouvoir se retirer. Lui refuser le retrait
 *    l'enfermerait dans une participation qu'il n'a pas demandee.
 *
 * Miroir exact de `inscription.utils.ts` cote backend, qui fait autorite :
 * l'API refusera de toute facon une action hors regle.
 */
export function getInscriptionState(event) {
  const now = new Date()
  const de = event?.inscriptionDe ? new Date(event.inscriptionDe) : null
  const jusquau = event?.inscriptionJusquau ? new Date(event.inscriptionJusquau) : null
  if (de && now < de) {
    return { code: 'AVANT', label: `Inscriptions ouvrent le ${fmtDateNum(de)}`, canRegister: false, canWithdraw: true }
  }
  if (jusquau && now > jusquau) {
    return { code: 'APRES', label: `Inscriptions closes depuis le ${fmtDateNum(jusquau)}`, canRegister: false, canWithdraw: false }
  }
  if (de || jusquau) {
    return {
      code: 'OUVERT',
      label: jusquau ? `Inscriptions jusqu'au ${fmtDateNum(jusquau)}` : 'Inscriptions ouvertes',
      canRegister: true,
      canWithdraw: true,
    }
  }
  // Pas de fenetre definie : repli historique sur le statut de l'evenement.
  if (['PLANIFIE', 'OUVERT'].includes(event?.statut)) {
    return { code: 'OUVERT', label: 'Inscriptions ouvertes', canRegister: true, canWithdraw: true }
  }
  return { code: 'FERME', label: 'Inscriptions fermées', canRegister: false, canWithdraw: false }
}

/**
 * « 05/09 » pour les BORNES d'inscription. Ce sont de vrais `DateTime` (saisis a
 * l'heure pres au BO), donc rendu LOCAL volontaire — a la difference de
 * `Event.date`, jour calendaire lu en UTC (cf. DESIGN-GUIDE §11.10).
 */
function fmtDateNum(date) {
  if (!date) return ''
  return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })
}
